// Beacon lane (Jaime-approved 2026-10-01): silent diagnostics for the
// "silent blank" (TH1) bug class. Built on the existing clientErrorReporter —
// kind tags ride in the report context and land as CLIENT_ERROR complaints
// server-side. Fire-and-forget, never throws, INVISIBLE to users (no UI copy).
// Two beacons, both session-deduped via sessionStorage:
//  - empty_success: unit-scoped home page returns 200 but the parsed list is
//    empty (would render the empty state).
//  - state_anomaly: the stale-unit machinery fired (staleUnitRepaired /
//    staleUnitBlocked) — already-recovered state, leading indicator of TH1.
import { reportClientError } from './clientErrorReporter';

const EMPTY_KEY = (route: string, unitId: string | null | undefined) =>
  `vecindus_beacon_empty:${route}:${unitId || '-'}`;
const HADDATA_KEY = (route: string, unitId: string | null | undefined) =>
  `vecindus_beacon_haddata:${route}:${unitId || '-'}`;
const STATE_KEY = (event: string) => `vecindus_beacon_state:${event}`;

function seen(key: string): boolean {
  try { return sessionStorage.getItem(key) === '1'; } catch { return false; }
}
function mark(key: string): void {
  try { sessionStorage.setItem(key, '1'); } catch { /* storage optional */ }
}

/**
 * Call on every successful unit-scoped list fetch (HTTP 200) from a Home*
 * page. Dedupes: max 1 per (route, unitId) per browser session.
 */
export function beaconEmptySuccess(args: { route: string; unitId: string | null | undefined; rowCount: number }): void {
  try {
    const { route, unitId, rowCount } = args;
    if (rowCount > 0) { mark(HADDATA_KEY(route, unitId)); return; }
    const key = EMPTY_KEY(route, unitId);
    if (seen(key)) return;
    mark(key);
    const hadDataBefore = seen(HADDATA_KEY(route, unitId));
    reportClientError({
      message: `EMPTY_SUCCESS ${route}`,
      level: 'info',
      context: {
        kind: 'empty_success',
        route,
        unitId: unitId || null,
        rowCount,
        hadUnitContext: !!unitId,
        // Noise discipline: without a prior non-empty fetch this session we
        // cannot distinguish a legitimately-empty brand-new unit from a bug —
        // still sent, tagged probable_first_use; severity stays P3 unless
        // repeated.
        probable_first_use: !hadDataBefore,
        snapshot: diagnosticSnapshot(),
      },
    });
  } catch { /* never throw */ }
}

/**
 * Call when the stale-unit machinery fires. Dedupes: max 1 per event type
 * per browser session.
 */
export function beaconStateAnomaly(args: {
  event: string;
  deadUnitId?: string | null;
  condoId?: string | null;
  fallbackUnitId?: string | null;
  repaired?: boolean;
}): void {
  try {
    const key = STATE_KEY(args.event);
    if (seen(key)) return;
    mark(key);
    reportClientError({
      message: `STATE_ANOMALY ${args.event}`,
      level: 'info',
      context: { kind: 'state_anomaly', ...args, snapshot: diagnosticSnapshot() },
    });
  } catch { /* never throw */ }
}

// ---------------------------------------------------------------------------
// CONTEXT SNAPSHOT (Jaime addition 2026-10-01 02:40): attach the user's
// client-side diagnostic state to every beacon so payloads show what actually
// happened in the account, not just the event. PRIVACY GUARD: technical state
// + UUIDs only — names/emails/phones/row data are never included; any string
// that looks like an email/URL is redacted.
// ---------------------------------------------------------------------------
import { SEL_TRACE_KEY } from '../utils/selTrace';
import { SENSITIVE_KEY_RE, scrubNameLike, scrubText } from './piiScrub';

/** Strings that must never ride in a beacon payload. (belt-and-braces final guard) */
function looksUnsafe(v: string): boolean {
  return /@|https?:\/\/|[\r\n]/.test(v);
}

/** Last ~20 selection-trace entries leading up to the event (high-value).
 * Each entry is pre-compacted to a short JSON string: the reporter sanitizer
 * caps object depth, and under context.snapshot the raw nested entries would
 * exceed it — flat strings survive sanitization intact. */
function traceTail(): string[] {
  try {
    const raw = sessionStorage.getItem(SEL_TRACE_KEY);
    const arr: unknown[] = raw ? JSON.parse(raw) : [];
    return (Array.isArray(arr) ? arr.slice(-20) : [])
      .map((e) => { try { return scrubText(JSON.stringify(e).slice(0, 200)); } catch { return scrubText(String(e).slice(0, 200)); } });
  } catch { return []; }
}

const STORAGE_KEY_PATTERNS: RegExp[] = [
  /^vecindus_active_condo$/,
  /^vecindus_active_owned_unit$/,
  /^vecindus_active_context$/,
  /^vecindus_last_condo$/,
  /^vecindus_last_unit_/,
  /^vecindus_known_unit_ids$/,
];

/** Relevant storage keys (ids only; id-array JSON collapsed to a CSV of ids). */
function storageSnapshot(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i) || '';
      if (!STORAGE_KEY_PATTERNS.some((re) => re.test(k))) continue;
      let v = localStorage.getItem(k) || '';
      try {
        const p = JSON.parse(v);
        if (Array.isArray(p)) v = p.filter((x) => typeof x === 'string').slice(0, 20).join(',');
        else if (typeof p === 'string') v = p;
        else if (p !== null && typeof p === 'object') {
          // Hardened PII guard (Task 2): drop sensitive keys, scrub the rest.
          const picked: Record<string, unknown> = {};
          for (const pk of Object.keys(p).slice(0, 15)) {
            if (SENSITIVE_KEY_RE.test(pk)) continue;
            picked[pk] = p[pk];
          }
          v = JSON.stringify(picked);
        }
      } catch { /* raw string */ }
      // scrubNameLike is safe here: snapshot values are technical ids, and a
      // seeded human name must never reach the payload.
      v = scrubNameLike(scrubText(v)).slice(0, 120);
      out[k] = looksUnsafe(v) ? '[redacted]' : v;
    }
  } catch { /* storage optional */ }
  return out;
}

/** Account shape summary: counts + presence only (ids/names never included). */
function accountShape(): Record<string, unknown> {
  try {
    let user: any = null;
    try {
      user = JSON.parse(localStorage.getItem('vecindus_auth') || localStorage.getItem('user') || 'null');
    } catch { user = null; }
    const contexts: any[] = Array.isArray(user?.contexts) ? user.contexts : [];
    const memberships: any[] = Array.isArray(user?.memberships) ? user.memberships : [];
    const condoLinkedUnits = new Set<string>();
    for (const c of contexts) if (c?.kind === 'condo' && c.unitId) condoLinkedUnits.add(String(c.unitId));
    for (const m of memberships) if (m?.unitId) condoLinkedUnits.add(String(m.unitId));
    return {
      contextCount: contexts.length,
      condoLinkedUnitCount: condoLinkedUnits.size,
      hasPersonalContext: contexts.some((c) => c?.kind === 'personal'),
    };
  } catch { return {}; }
}

/**
 * Visible-error beacon (Jaime 2026-10-01 02:42): fires when an actual error
 * banner is rendered on the user's screen (LoadError in
 * components/HomeStateBanners.tsx).
 *
 * SURFACE OWNERSHIP (no double-reporting):
 *  - LoadError / visible_error (here): owns unit-scoped /home/* fetch
 *    failures — these render the banner. The api.ts response interceptor
 *    deliberately SKIPS reportApiFailure for /home/* URLs for this reason.
 *  - reportApiFailure (api.ts interceptor): owns every other failed axios
 *    call (non-/home/*).
 *  - toast.ts: console-only by design — it does NOT report, so toasts and
 *    banners never double-report the same failure.
 *
 * Controlled states are skipped: offline/503 banners are reported
 * distinctly elsewhere, and unauthenticated (401) views are not errors.
 * Dedupe: max 1 per (route, normalized message) per browser session.
 */
const VISIBLE_KEY = (route: string, message: string) =>
  `vecindus_beacon_visible:${route}:${message.toLowerCase().replace(/\s+/g, ' ').trim().slice(0, 120)}`;

const CONTROLLED_STATE_RE = /(503|offline|sin conexi(?:ó|o)n|intenta de nuevo m(?:á|a)s tarde)|\b401\b|inicia sesi(?:ó|o)n|sesi(?:ó|o)n expirada/i;

export function beaconVisibleError(args: { route: string; message: string; unitId?: string | null }): void {
  try {
    const { route, message } = args;
    if (!message || CONTROLLED_STATE_RE.test(message)) return;
    const key = VISIBLE_KEY(route, message);
    if (seen(key)) return;
    mark(key);
    reportClientError({
      message: `VISIBLE_ERROR ${route}: ${message.slice(0, 120)}`,
      level: 'error',
      context: {
        kind: 'visible_error',
        route,
        message: scrubText(message.slice(0, 300)),
        unitId: args.unitId || null,
        snapshot: diagnosticSnapshot(),
      },
    });
  } catch { /* never throw */ }
}

/** Bounded diagnostic snapshot attached to every beacon payload. */
export function diagnosticSnapshot(): Record<string, unknown> {
  let buildSha = 'unknown';
  let appVersion = 'unknown';
  try { if (typeof __BUILD_SHA__ !== 'undefined') buildSha = __BUILD_SHA__; } catch { /* keep */ }
  try { if (typeof __APP_VERSION__ !== 'undefined') appVersion = __APP_VERSION__; } catch { /* keep */ }
  return {
    buildSha,
    appVersion,
    traceTail: traceTail(),
    storage: storageSnapshot(),
    account: accountShape(),
  };
}
